import React, { useState, useEffect } from 'react';
import ClassicMainMenu from './components/ClassicMainMenu';
import ClassicJournalEntry from './components/ClassicJournalEntryDebug';
import ClassicKamokuManagement from './components/ClassicKamokuManagement';
import HojoKamokuManagement from './components/HojoKamokuManagement';
import ClassicDaikubunManagement from './components/ClassicDaikubunManagement';
import ClassicJournalDisplay from './components/ClassicJournalDisplay';
import ClassicScreenShell from './components/ClassicScreenShell';
import ClassicPlaceholder from './components/ClassicPlaceholder';
import ReportOutput from './components/ReportOutput';
import ClassicGeneralLedger from './components/ClassicGeneralLedger';
import ClassicUserManagement from './components/ClassicUserManagement';
import JournalImport from './components/JournalImport';
import FixedAssetManagement from './components/FixedAssetManagement';
import ClassicMonthlySummary from './components/ClassicMonthlySummary';
import TrialBalance from './components/TrialBalance';
import SubLedger from './components/SubLedger';
import ClassicTekiyouManagement from './components/ClassicTekiyouManagement';
import ClassicOrganizationInfo from './components/ClassicOrganizationInfo';
import ClassicFiscalPeriod from './components/ClassicFiscalPeriod';
import BudgetManagement from './components/BudgetManagement';
import BackupRestore from './components/BackupRestore';
import { VERSION_INFO } from './constants/version';
import { getShortVersion } from './utils/version';
import './App.css';
import { getErrorMessage } from './utils/errorHandler';
import { filterTenantsForDisplay, LAST_SELECTED_TENANT_ID_KEY } from './utils/tenantDisplay';
import TenantSwitcher from './components/TenantSwitcher';
import OrganizationTree from './components/OrganizationTree';
import AnnualProcess from './components/AnnualProcess';
import SystemClose from './components/SystemClose';
import NoteManagement from './components/NoteManagement';
import KokuhoRenPlaceholder from './components/KokuhoRenPlaceholder';
import ElectronicDisclosurePlaceholder from './components/ElectronicDisclosurePlaceholder';
import MasterImport from './components/MasterImport';
import ComingSoon from './components/ComingSoon';
import AuditLogViewer from './components/AuditLogViewer';
import CashBankLedger from './components/CashBankLedger';
import CashFlowAnalysis from './pages/analysis/CashFlowAnalysis';
import DepartmentReport from './pages/analysis/DepartmentReport';
import AdminPanel from './components/AdminPanel';

import KokuhoRenExport from './components/KokuhoRenExport';
import ElectronicDisclosure from './components/ElectronicDisclosure';
import RealEstateLedger from './pages/ledgers/RealEstateLedger';
import ManagementIndicators from './pages/analysis/ManagementIndicators';

// 基本的な型定義
interface TenantInfo {
  id: number | null;
  tenant_code: string | null;
  tenant_name: string | null;
  path?: string | null;
  role: string;
}

interface AuthUser {
  id: number;
  username: string;
  email: string;
  fullName: string;
  role: string;
  permissions: string[];
}

type RefreshTenantOptions = {
  fallbackTenants?: TenantInfo[];
  enableFetch?: boolean;
};

const App: React.FC = () => {
  // 基本状態管理
  const [activeMenu, setActiveMenu] = useState<string>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [navParams, setNavParams] = useState<any>({}); // For passing params between screens
  const [selectedYear, setSelectedYear] = useState<number>(() => {
    const saved = localStorage.getItem('selectedFiscalYear');
    return saved ? parseInt(saved, 10) : 2025;
  }); // CR-007

  useEffect(() => {
    localStorage.setItem('selectedFiscalYear', selectedYear.toString());
  }, [selectedYear]);
  const [mainMenuCategory, setMainMenuCategory] = useState<string | null>('日常処理'); // メニューの選択状態を維持

  const handleMenuClick = (menuId: string) => {
    setActiveMenu(menuId);
    setNavParams({}); // Reset params on menu change
  };

  const handleNavigate = (menu: string, params?: any) => {
    setActiveMenu(menu);
    if (params) {
      setNavParams(params);
    }
  };
  // 認証関連
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [accessibleTenants, setAccessibleTenants] = useState<TenantInfo[]>([]);
  const [currentTenant, setCurrentTenant] = useState<TenantInfo | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [showHierarchy, setShowHierarchy] = useState(false);

  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const API_BASE = process.env.REACT_APP_API_BASE || '';

  const normalizeTenant = (raw: any): TenantInfo | null => {
    if (!raw) return null;
    const rawId = raw.tenant_id ?? raw.id ?? null;
    const id = rawId === null || rawId === undefined ? null : Number(rawId);
    return {
      id,
      tenant_code: raw.tenant_code ?? null,
      tenant_name: raw.tenant_name ?? null,
      path: raw.path ?? raw.tenant_name ?? null,
      role: raw.role ?? 'tenant_user',
    };
  };

  const buildTenantList = (rawList: any): TenantInfo[] => {
    if (!Array.isArray(rawList)) {
      return [];
    }
    return rawList
      .map(normalizeTenant)
      .filter((item: TenantInfo | null): item is TenantInfo => item !== null);
  };

  const applyTenant = (tenant: TenantInfo | null) => {
    setCurrentTenant(tenant);
  };

  const mapUserInfo = (raw: any): AuthUser => ({
    id: Number(raw.id),
    username: raw.username,
    email: raw.email,
    fullName: raw.full_name ?? '',
    role: raw.global_role ?? raw.role ?? '',
    permissions: raw.permissions ?? [],
  });

  const applyUserState = (raw: any) => {
    setCurrentUser(mapUserInfo(raw));
    const tenants = buildTenantList(raw.accessible_tenants);
    setAccessibleTenants(tenants);
    const tenant = normalizeTenant(raw.current_tenant) ?? tenants[0] ?? null;
    applyTenant(tenant);
    setGlobalError(null);
    setLoginError(null);
  };

  const fetchUserProfile = async (authToken: string) => {
    const res = await fetch(`${API_BASE}/auth/me`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!res.ok) {
      const message = await getErrorMessage(res, 'ユーザー情報の取得に失敗しました');
      throw new Error(message);
    }

    return await res.json();
  };

  const fetchAccessibleTenants = async (authToken: string): Promise<TenantInfo[]> => {
    const res = await fetch(`${API_BASE}/api/tenants`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    });

    if (!res.ok) {
      const message = await getErrorMessage(res, 'テナント一覧の取得に失敗しました');
      const error: any = new Error(message);
      error.status = res.status;
      throw error;
    }

    const data = await res.json();
    return buildTenantList(data.tenants);
  };

  const refreshAccessibleTenants = async (
    authToken: string,
    preferTenantId?: number | null,
    options: RefreshTenantOptions = {},
  ) => {
    const allowFetch = options.enableFetch ?? true;
    const fallbackTenants = options.fallbackTenants ?? accessibleTenants;

    const selectTenant = (tenants: TenantInfo[]) => {
      if (!tenants || tenants.length === 0) {
        applyTenant(null);
        return;
      }

      const storedRaw = localStorage.getItem(LAST_SELECTED_TENANT_ID_KEY);
      const storedId = storedRaw ? parseInt(storedRaw, 10) : NaN;
      const storedValid = !Number.isNaN(storedId) && tenants.some((t) => t.id === storedId);

      let targetId: number | null = null;
      if (storedValid) {
        targetId = storedId;
      } else if (preferTenantId !== undefined && preferTenantId !== null) {
        const matchPref = tenants.find((tenant) => tenant.id === preferTenantId);
        targetId = matchPref ? preferTenantId : null;
      }

      if (targetId == null) {
        targetId = tenants[0]?.id ?? null;
      }

      if (targetId == null) {
        applyTenant(tenants[0] ?? null);
        return;
      }

      const match = tenants.find((tenant) => tenant.id === targetId);
      const chosen = match ?? tenants[0] ?? null;
      applyTenant(chosen);
      if (chosen?.id != null) {
        localStorage.setItem(LAST_SELECTED_TENANT_ID_KEY, String(chosen.id));
      }
    };

    const applyTenants = (tenants: TenantInfo[], clearError: boolean) => {
      setAccessibleTenants(tenants);
      selectTenant(tenants);
      if (clearError) {
        setGlobalError(null);
      }
      return tenants;
    };

    const applyFallback = (clearError: boolean) => {
      if (fallbackTenants && fallbackTenants.length > 0) {
        return applyTenants(fallbackTenants, clearError);
      }
      setAccessibleTenants([]);
      applyTenant(null);
      if (clearError) {
        setGlobalError(null);
      }
      return [];
    };

    if (!allowFetch) {
      return applyFallback(true);
    }

    try {
      const tenants = await fetchAccessibleTenants(authToken);
      if (!tenants || tenants.length === 0) {
        return applyFallback(true);
      }
      return applyTenants(tenants, true);
    } catch (err: any) {
      const status = err?.status;
      // 403 or other fetch errors, try to use local data if available
      console.warn('Tenant API fetch failed, falling back to local data', err);

      if (fallbackTenants && fallbackTenants.length > 0) {
        return applyTenants(fallbackTenants, true); // true to clear error if fallback succeeds
      }

      const message = err?.message || 'テナント一覧の取得に失敗しました。';
      setGlobalError(message);
      return applyFallback(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setToken(null);
    setCurrentUser(null);
    setAccessibleTenants([]);
    applyTenant(null);
    setGlobalError(null);
    setActiveMenu('login');
  };

  // 認証付きfetch
  const apiFetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const headers = new Headers(init.headers || {});
    headers.set('Content-Type', headers.get('Content-Type') || 'application/json');
    const effectiveToken = token || localStorage.getItem('auth_token');
    if (effectiveToken) {
      headers.set('Authorization', `Bearer ${effectiveToken}`);
    }
    // URLが相対パスの場合、API_BASEを付加
    const url = typeof input === 'string' && input.startsWith('/')
      ? `${API_BASE}${input}`
      : input;
    return await fetch(url, { ...init, headers });
  };

  const loadCurrentUser = async (authToken: string, options: { redirectToMain?: boolean } = {}) => {
    try {
      const profile = await fetchUserProfile(authToken);
      applyUserState(profile);
      const normalizedCurrentTenant = normalizeTenant(profile.current_tenant);
      const fallbackTenants = buildTenantList(profile.accessible_tenants);
      const userRole = profile?.global_role ?? profile?.role ?? '';
      const tenants = await refreshAccessibleTenants(
        authToken,
        normalizedCurrentTenant?.id ?? null,
        {
          fallbackTenants,
          enableFetch: userRole === 'super_admin' || userRole === 'admin',
        },
      );

      if (!tenants || tenants.length === 0) {
        throw new Error('利用可能な保育園（テナント）が割り当てられていません。管理者に連絡してください。');
      }

      if (options.redirectToMain !== false) {
        setActiveMenu('main-menu');
      }
    } catch (err: any) {
      const message = err?.message || 'セッションの復元に失敗しました。';
      setGlobalError(message);
      setLoginError(message);
      handleLogout();
    }
  };

  // ログイン処理
  const handleLogin = async (loginUsername: string, loginPassword: string) => {
    setLoginLoading(true);
    setLoginError(null);
    setGlobalError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginUsername, password: loginPassword }),
      });

      if (!res.ok) {
        const message = await getErrorMessage(res);
        throw new Error(message);
      }

      const data = await res.json();
      localStorage.setItem('auth_token', data.access_token);
      setToken(data.access_token);
      applyUserState(data.user_info);
      const normalizedCurrentTenant = normalizeTenant(data.user_info?.current_tenant);
      const fallbackTenants = buildTenantList(data.user_info?.accessible_tenants);
      const userRole = data.user_info?.global_role ?? data.user_info?.role ?? '';
      const tenants = await refreshAccessibleTenants(
        data.access_token,
        normalizedCurrentTenant?.id ?? null,
        {
          fallbackTenants,
          enableFetch: userRole === 'super_admin' || userRole === 'admin',
        },
      );

      if (!tenants || tenants.length === 0) {
        throw new Error('利用可能な保育園（テナント）が割り当てられていません。管理者に連絡してください。');
      }

      setActiveMenu('main-menu');
    } catch (e: any) {
      console.error("Login failed", e);
      let msg = e?.message || 'ログインに失敗しました。';
      if (msg === 'Failed to fetch') {
        msg = 'サーバーに接続できませんでした。バックエンドが起動しているか確認してください。';
      }
      setLoginError(msg);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleTenantSwitchSelect = async (tenantId: number) => {
    if (currentTenant?.id === tenantId) {
      return;
    }

    const authToken = token || localStorage.getItem('auth_token');
    if (!authToken) {
      handleLogout();
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/tenants/switch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ tenant_id: tenantId }),
      });

      if (!res.ok) {
        const message = await getErrorMessage(res, 'テナント切り替えに失敗しました。');
        throw new Error(message);
      }

      const data = await res.json();
      const newToken: string = data.new_token;

      if (newToken) {
        localStorage.setItem('auth_token', newToken);
        localStorage.setItem(LAST_SELECTED_TENANT_ID_KEY, String(tenantId));
        setToken(newToken);
        await loadCurrentUser(newToken, { redirectToMain: false });
      } else {
        await loadCurrentUser(authToken, { redirectToMain: false });
      }

      setGlobalError(null);
    } catch (err: any) {
      const message = err?.message || 'テナント切り替えに失敗しました。';
      setGlobalError(message);
      if (message.includes('再度ログイン')) {
        setLoginError(message);
      }
    }
  };

  const handleTenantSwitcherUpdate = async (newToken: string, tenantInfo?: any) => {
    if (!newToken) {
      console.error("New token is empty in handleTenantSwitcherUpdate");
      return;
    }
    try {
      // 直前まで「前の保育園」として覚えていた情報が残っていると、切り替え後も画面上の「いまの園」が更新されないことがあったため、先に「今選んだ園」に合わせてからユーザー情報を読み直す
      const tid = tenantInfo?.tenant_id ?? tenantInfo?.id;
      if (tid != null && !Number.isNaN(Number(tid))) {
        localStorage.setItem(LAST_SELECTED_TENANT_ID_KEY, String(tid));
      }
      localStorage.setItem('auth_token', newToken);
      setToken(newToken);
      // Don't fully reload user if we just switched tenant context, just update state
      // But verify token validity by fetching profile
      await loadCurrentUser(newToken, { redirectToMain: true });
      setGlobalError(null);
    } catch (err: any) {
      console.error("Failed to update tenant context", err);
      const message = err?.message || 'テナント情報の更新に失敗しました。再度ログインしてください。';
      setGlobalError(message);
      // Only set login error if it's a critical auth failure
      if (err?.status === 401) {
        setLoginError(message);
        handleLogout();
      }
    }
  };

  // メニュー選択処理
  const handleMenuSelect = (menuId: string) => {
    setActiveMenu(menuId);
  };

  // 初期化時にトークンチェック
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    if (!storedToken) {
      return;
    }

    try {
      const tokenData = JSON.parse(atob(storedToken.split('.')[1]));
      const now = Date.now() / 1000;

      if (tokenData.exp > now) {
        setToken(storedToken);
        loadCurrentUser(storedToken);
      } else {
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
    }
  }, []);

  // メインコンテンツのレンダリング
  const renderMainContent = () => {
    const isSuperAdmin = (currentUser?.role ?? '').toLowerCase() === 'super_admin';
    const tenantsForUi = filterTenantsForDisplay(accessibleTenants, currentTenant?.id ?? null);
    const headerStatus = currentUser ? (
      <div style={{ fontSize: '11px', textAlign: 'right', color: '#ffffcc', lineHeight: 1.6 }}>
        <div>{currentUser.fullName || currentUser.username}</div>
        {(accessibleTenants.length > 0 || isSuperAdmin || currentUser?.role === 'admin') ? (
          <div style={{ marginTop: '6px' }}>
            <TenantSwitcher
              apiFetch={async (url: string, options?: RequestInit) => {
                const response = await apiFetch(url, options);
                if (!response.ok) {
                  const message = await getErrorMessage(response);
                  throw new Error(message);
                }
                return response.json();
              }}
              onTenantSwitch={handleTenantSwitcherUpdate}
              currentUser={currentUser}
              currentTenant={currentTenant}
              accessibleTenants={tenantsForUi}
            />
            <button
              onClick={() => setShowHierarchy(!showHierarchy)}
              style={{
                marginTop: '6px',
                padding: '4px 8px',
                backgroundColor: '#6b5eae',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '11px',
                display: 'block',
                width: '100%'
              }}
            >
              {showHierarchy ? '✕ 閉じる' : '🌿 組織階層を表示'}
            </button>
            {showHierarchy && (
              <div style={{
                position: 'fixed',
                top: '60px',
                right: '20px',
                zIndex: 2000,
                width: '300px',
                color: '#333'
              }}>
                <OrganizationTree
                  apiFetch={async (url: string, options?: RequestInit) => {
                    const response = await apiFetch(url, options);
                    if (!response.ok) {
                      const message = await getErrorMessage(response);
                      throw new Error(message);
                    }
                    return response.json();
                  }}
                  onSelect={(id) => {
                    handleTenantSwitchSelect(id);
                    setShowHierarchy(false);
                  }}
                  selectedId={currentTenant?.id ?? undefined}
                />
              </div>
            )}
          </div>
        ) : accessibleTenants.length > 1 ? (
          <select
            value={currentTenant?.id !== null && currentTenant?.id !== undefined ? String(currentTenant.id) : ''}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (!nextValue) {
                return;
              }
              const nextId = Number(nextValue);
              if (Number.isNaN(nextId)) {
                return;
              }
              handleTenantSwitchSelect(nextId);
            }}
            style={{
              marginTop: '6px',
              padding: '2px 6px',
              borderRadius: '4px',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              color: '#fff',
              fontSize: '11px',
            }}
          >
            {tenantsForUi.map((tenant) => (
              <option key={`${tenant.id ?? 'all'}`} value={tenant.id ?? ''}>
                {tenant.tenant_name || 'テナント未選択'}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ fontSize: '11px', marginTop: '6px' }}>
            {currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
          </div>
        )}
        {globalError && (
          <div style={{ marginTop: '6px', color: '#ffb3b3', fontSize: '10px' }}>
            {globalError}
          </div>
        )}
      </div>
    ) : undefined;

    const simpleHeaderRight = currentUser ? (
      <div style={{ fontSize: '11px', textAlign: 'right', color: '#ffffcc', lineHeight: 1.6 }}>
        <div>{currentUser.fullName || currentUser.username}</div>
      </div>
    ) : undefined;

    const renderClassicScreen = (
      title: string,
      content: React.ReactNode,
    ) => (
      <ClassicScreenShell
        title={title}
        tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        onBack={() => setActiveMenu('main-menu')}
        rightContent={headerStatus}
      >
        {content}
      </ClassicScreenShell>
    );

    const renderPlaceholder = (
      title: string,
      description?: string,
      notes?: string[],
    ) => (
      <ClassicPlaceholder
        title={title}
        description={description}
        notes={notes}
        onBack={() => setActiveMenu('main-menu')}
      />
    );

    switch (activeMenu) {
      case 'login':
        return (
          <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'Arial, sans-serif'
          }}>
            <div style={{
              backgroundColor: '#ffffff',
              padding: '40px',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              width: '100%',
              maxWidth: '400px'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                <h1 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#2c3e50',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
                }}>
                  KI会計システム
                </h1>
              </div>
              <div style={{
                backgroundColor: '#ffffff',
                padding: '20px',
                borderRadius: '8px'
              }}>
                <h2 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#2c3e50' }}>
                  🔐 ログイン
                </h2>

                {loginError && (
                  <div style={{
                    backgroundColor: '#fee',
                    color: '#c33',
                    padding: '12px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    fontSize: '14px'
                  }}>
                    {loginError}
                  </div>
                )}

                <label style={{ display: 'block', marginBottom: '16px' }}>
                  <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                    ユーザー名
                  </span>
                  <input
                    type="text"
                    placeholder="admin"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px'
                    }}
                  />
                </label>

                <label style={{ display: 'block', marginBottom: '20px' }}>
                  <span style={{ display: 'block', marginBottom: '4px', fontSize: '14px', color: '#555' }}>
                    パスワード
                  </span>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px 40px 10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#6b7280',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title={showPassword ? "パスワードを隠す" : "パスワードを表示"}
                    >
                      {showPassword ? '👁️' : '🔒'}
                    </button>
                  </div>
                </label>

                <button
                  type="button"
                  onClick={() => handleLogin(username, password)}
                  disabled={loginLoading}
                  style={{
                    width: '100%',
                    background: '#6366f1',
                    color: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '500'
                  }}
                >
                  {loginLoading ? 'ログイン中...' : 'ログイン'}
                </button>

                {process.env.NODE_ENV !== 'production' && (
                  <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '12px',
                    color: '#6c757d',
                  }}>
                    テスト用: USER <code>admin</code> / PASSWORD <code>Admin@123!</code>
                  </div>
                )}

                <div style={{
                  marginTop: '16px',
                  padding: '8px',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px',
                  fontSize: '11px',
                  color: '#495057',
                  textAlign: 'center',
                  borderTop: '1px solid #dee2e6'
                }}>
                  <div>{VERSION_INFO.description}</div>
                  <div>Version {VERSION_INFO.version} ({VERSION_INFO.buildDate})</div>
                  <div style={{ marginTop: '2px', fontSize: '10px' }}>{getShortVersion()}</div>
                  <div style={{ marginTop: '4px', fontSize: '10px' }}>{VERSION_INFO.copyright}</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'main-menu':
        return (
          <ClassicMainMenu
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            currentUser={currentUser}
            onLogout={handleLogout}
            headerRightContent={headerStatus}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            initialCategory={mainMenuCategory}
            onCategoryChange={setMainMenuCategory}
          />
        );

      case 'journal-entry':
        return renderClassicScreen(
          '1: 会計伝票登録',
          <ClassicJournalEntry
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            selectedYear={selectedYear}
          />,
        );

      case 'journal-display':
        return renderClassicScreen(
          '2: 伝票照会',
          <ClassicJournalDisplay
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            selectedYear={selectedYear}
          />,
        );

      case 'cash-bank-ledger':
        return renderClassicScreen(
          '3: 現預金出納帳',
          <CashBankLedger
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'audit-logs':
        return renderClassicScreen(
          '99: 監査ログ',
          <AuditLogViewer />
        );

      case 'kamoku-management':
        return renderClassicScreen(
          '71: 勘定科目マスタ',
          <ClassicKamokuManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'hojo-master':
        return renderClassicScreen(
          '74: 補助科目マスタ',
          <HojoKamokuManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'daikubun-management':
        return renderClassicScreen(
          '72: 大区分マスタ',
          <ClassicDaikubunManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'cash-flow-analysis':
        return renderClassicScreen(
          'キャッシュフロー分析',
          <CashFlowAnalysis
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            selectedYear={selectedYear}
          />,
        );

      case 'department-report':
        return renderClassicScreen(
          '部門別収支報告',
          <DepartmentReport
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            selectedYear={selectedYear}
          />,
        );

      case 'report-output':
        return renderClassicScreen(
          '21: 帳票出力・書式の選択',
          <ReportOutput
            apiBase={API_BASE}
            onClose={() => setActiveMenu('main-menu')}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || ''}
            currentUser={currentUser}
            selectedYear={selectedYear}
          />,
        );

      case 'notes-management':
        return renderClassicScreen(
          '注記管理',
          <NoteManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'csv-output':
        return renderClassicScreen(
          '22: 帳票出力 (CSV出力)',
          <ReportOutput
            apiBase={API_BASE}
            onClose={() => setActiveMenu('main-menu')}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || ''}
            currentUser={currentUser}
            selectedYear={selectedYear}
          />,
        );

      case 'notes-management':
        return renderClassicScreen(
          '注記管理',
          <NoteManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'journal-edit':
        return renderClassicScreen(
          '2: 伝票の修正・削除',
          <ClassicJournalDisplay
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            selectedYear={selectedYear}
          />,
        );

      case 'journal-import':
        return renderClassicScreen(
          '仕訳インポート',
          <JournalImport
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'monthly-summary':
        return renderClassicScreen(
          '3: 月収支確認',
          <ClassicMonthlySummary
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'general-ledger':
        return renderClassicScreen(
          '11: 総勘定元帳',
          <ClassicGeneralLedger
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'trial-balance':
        return renderClassicScreen(
          '試算表',
          <TrialBalance
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id || null}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || ''}
            onNavigate={handleNavigate}
          />,
        );

      case 'user-management':
        return renderClassicScreen(
          '91: ユーザー管理',
          <ClassicUserManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
          />,
        );

      case 'sub-ledger':
        return renderClassicScreen(
          '14: 補助元帳',
          <SubLedger
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'tekiyou-master':
        return renderClassicScreen(
          '73: 摘要マスタ',
          <ClassicTekiyouManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'revenue-category':
        // 収支区分設定は勘定科目マスタ画面で設定可能
        return renderClassicScreen(
          '71: 勘定科目マスタ（収支区分設定）',
          <ClassicKamokuManagement
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'organization-info':
        return renderClassicScreen(
          '81: 法人・施設情報',
          <ClassicOrganizationInfo
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'fiscal-period':
        return renderClassicScreen(
          '82: 会計期間設定',
          <ClassicFiscalPeriod
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'budget-management':
        return renderClassicScreen(
          '61: 予算管理',
          <BudgetManagement
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id || null}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || ''}
            onNavigate={handleNavigate}
          />,
        );

      case 'budget-actual':
        return renderClassicScreen(
          '62: 予算実績対比',
          <BudgetManagement
            initialTab={1}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id || null}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || ''}
            onNavigate={handleNavigate}
          />,
        );

      case 'carry-forward':
        return renderClassicScreen(
          '63: 前期繰越・年次更新',
          <AnnualProcess
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'fixed-asset':
        return renderClassicScreen(
          '固定資産管理',
          <FixedAssetManagement
            onBack={() => setActiveMenu('main-menu')}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'real-estate-ledger':
        return renderClassicScreen(
          '不動産台帳',
          <RealEstateLedger
            onBack={() => setActiveMenu('main-menu')}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'system-close':
        return renderClassicScreen(
          '83: 締切処理',
          <SystemClose
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'system-admin':
        return renderClassicScreen(
          '91: テナント管理',
          <AdminPanel
            apiFetch={apiFetch}
          />,
        );

      case 'master-import':
        return renderClassicScreen(
          'マスタ一括取込',
          <MasterImport
            onMenuSelect={handleMenuSelect}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            currentUser={currentUser}
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
          />,
        );

      case 'backup-restore':
        return renderClassicScreen(
          'データバックアップ・復元',
          <BackupRestore
            apiFetch={apiFetch}
            currentTenantId={currentTenant?.id ?? null}
            currentUser={currentUser}
          />,
        );

      case 'kokuho-ren':
        return renderClassicScreen('国保連提出資料', <KokuhoRenExport onNavigate={handleMenuSelect} />);
      case 'xbrl-disclosure':
        return renderClassicScreen(
          '財務諸表等電子開示（WAM NET用CSV）',
          <ElectronicDisclosure
            apiFetch={apiFetch}
            selectedYear={selectedYear}
            onBack={() => setActiveMenu('main-menu')}
          />
        );
      case 'audit-submission':
        return renderClassicScreen('監査法人提出資料', <ComingSoon title="監査法人提出資料" description="監査法人提出用の資料一式を出力する機能です。" />);
      case 'management-analysis':
        return renderClassicScreen(
          '経営指標分析',
          <ManagementIndicators
            onBack={() => setActiveMenu('main-menu')}
            tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
            selectedYear={selectedYear}
            apiFetch={apiFetch}
          />
        );

      default:
        if (currentUser) {
          return (
            <ClassicMainMenu
              onMenuSelect={handleMenuSelect}
              tenantName={currentTenant?.path || currentTenant?.tenant_name || 'テナント未選択'}
              selectedYear={selectedYear}
              onYearChange={setSelectedYear}
              currentUser={currentUser}
              onLogout={handleLogout}
              headerRightContent={headerStatus}
              apiFetch={apiFetch}
              currentTenantId={currentTenant?.id ?? null}
              initialCategory={mainMenuCategory}
              onCategoryChange={setMainMenuCategory}
            />
          );
        }
        return null;
    }
  };

  return (
    <div className="App" style={{ margin: 0, padding: 0, minHeight: '100vh' }}>
      {renderMainContent()}
    </div>
  );
};

export default App;
